import React, { useState, useEffect, useRef } from 'react';
import { WebsiteDesignProps } from './types';
import { 
  BookOpen, Menu, X, ArrowRight, Star, 
  Users, Trophy, School, CheckCircle2,
  Facebook, Instagram, Linkedin, Youtube, Phone, Mail, MapPin, 
  Clock, Globe, ChevronLeft, ChevronDown, Quote, ArrowUpRight, 
  MessageCircle, Award, Target, Download, Building2, HelpCircle, Info, Image as ImageIcon
} from 'lucide-react';
import { Button } from "@/components/ui/button";
import { VisualImageEditable } from "@/components/VisualImageEditable";
import { VisualTextEditable } from "@/components/VisualTextEditable";

const Design0_Features: React.FC<WebsiteDesignProps> = ({ previewData, isEditMode, onUpdateField }) => {
  const [activeTab, setActiveTab] = useState('All');
  const [isScrolled, setIsScrolled] = useState(false);
  const [showAllStaff, setShowAllStaff] = useState(false);
  const [expandedView, setExpandedView] = useState<'staff' | 'board' | 'gallery' | 'facilities' | 'anthems' | null>(null);
  const [activeFaq, setActiveFaq] = useState<number | null>(null);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const safeUrl = (file: any) => {
    if (!file) return undefined;
    if (typeof file === 'string') return file;
    try {
      return URL.createObjectURL(file);
    } catch (e) {
      return undefined;
    }
  };

  const schoolName = previewData?.name || "School Name";
  
  // Professional Institutional Data Structure
  // Exact Mapping from AddWebsiteDataForm Schema
  const data = {
    // 1. General & 2. Main Photos
    name: previewData?.name || "St. Augustine's National Academy",
    motto: previewData?.motto || "In Veritate et Virtute (In Truth and Virtue)",
    logo: previewData?.logo ? safeUrl(previewData.logo) : "https://images.unsplash.com/photo-1599305090598-fe179d501227?q=80&w=256&auto=format&fit=crop",
    headerPhotos: (previewData?.headerPhotos && previewData.headerPhotos.filter((p: any) => p !== null).length > 0) ? previewData.headerPhotos : [
      "https://images.unsplash.com/photo-1523050854058-8df90110c9f1?q=80&w=1200&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1541339907198-e08756ebafe3?q=80&w=1200&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1519331379826-f10be5486c6f?q=80&w=1200&auto=format&fit=crop"
    ],
    
    // 3. Stats
    stats: {
      students: previewData?.totalStudents || "1,450",
      staff: previewData?.totalStaff || "92",
      founded: previewData?.yearFounded || "1974",
      alumni: previewData?.totalAlumni || "8,000+"
    },

    // 4. Principal
    principal: {
      name: previewData?.principalName || "Dr. Richard M. Mutua",
      message: previewData?.principalMessage || "Welcome to St. Augustine's, a leading institution where we harmonize academic discipline with character development to mold the leaders of tomorrow.",
      photo: previewData?.principalPhoto ? safeUrl(previewData.principalPhoto) : "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?q=80&w=400&auto=format&fit=crop"
    },
    
    // ... narratives ...
    mission: previewData?.mission || "To provide a holistic and inclusive learning environment that fosters academic brilliance, integrity, and global citizenship.",
    vision: previewData?.vision || "To be the premier national center of excellence, recognized globally for academic innovation and transformative leadership.",
    about: previewData?.about || "A distinguished national institution dedicated to the pursuit of excellence in all spheres of life.",
    history: previewData?.historyText || "Established in 1974 as a community initiative, our school was later elevated to National status in 2011, continuing a heritage of producing top-tier professionals and leaders for the continent.",
    aboutPhoto: previewData?.aboutPhoto ? safeUrl(previewData.aboutPhoto) : "https://images.unsplash.com/photo-1577896851231-70ef18881754?q=80&w=2070",
    missionPhoto: previewData?.missionPhoto ? safeUrl(previewData.missionPhoto) : "https://images.unsplash.com/photo-1524178232363-1fb2b075b655?q=80&w=2070",
    visionPhoto: previewData?.visionPhoto ? safeUrl(previewData.visionPhoto) : "https://images.unsplash.com/photo-1544928147-79a2dbc1f389?q=80&w=2070",
    whyChooseUsPhoto: previewData?.whyChooseUsPhoto ? safeUrl(previewData.whyChooseUsPhoto) : "https://images.unsplash.com/photo-1503676260728-1c00da094a0b?q=80&w=2070",
    nationalAnthem: previewData?.nationalAnthem || "O God of all creation\nBless this our land and nation\nJustice be our shield and defender\nMay we dwell in unity\nPeace and liberty\nPlenty be found within our borders.\n\nLet one and all arise\nWith hearts both strong and true\nService be our earnest endeavour\nAnd our homeland of Kenya\nHeritage of splendour\nFirm may we stand to defend.\n\nLet all with one accord\nIn common bond united\nBuild this our nation together\nAnd the glory of Kenya\nThe fruit of our labour\nFill every heart with thanksgiving.",
    schoolAnthem: previewData?.schoolAnthem || "Hail to our alma mater, standing tall and true,\nGuiding lights of knowledge, red and brilliant blue.\nIn these halls of wisdom, we learn to lead and serve,\nBuilding strong foundations, with unyielding nerve.\n\nThrough the trials and triumphs, together we will rise,\nReaching for the future, beneath the open skies.\nHonor, truth, and courage, forever be our guide,\nIn our school's great legacy, we take eternal pride!",
    whyUs: previewData?.whyChooseUs || "We combine a tradition of excellence with state-of-the-art facilities and a passionate faculty dedicated to cross-disciplinary learning.",

    coreValues: previewData?.coreValues || ["Integrity", "Excellence", "Innovation", "Inclusivity", "Service"],
    quote: {
      text: previewData?.quote || "The beautiful thing about learning is that no one can take it away from you.",
      author: previewData?.quoteAuthor || "B.B. King"
    },

    curriculum: {
      description: previewData?.curriculumDesc || "Our comprehensive curriculum follows the National Competency Based framework, enhanced with international STEM integration for a global perspective.",
      compulsory: previewData?.compulsorySubjects || ["English", "Mathematics", "Kiswahili", "Chemistry", "Biology", "Physics"],
      optional: previewData?.optionalSubjects || ["Computer Studies", "French", "Business Studies", "Agriculture", "Music", "Aviation"],
      photo: previewData?.curriculumPhoto ? safeUrl(previewData.curriculumPhoto) : "https://images.unsplash.com/photo-1577896851231-70ef18881754?q=80&w=2070"
    },

    activities: (previewData?.activities && previewData.activities.length > 0) ? previewData.activities : [
      { id: "1", title: "Robotics Club", description: "Design and program modern robots with Python.", photo: "https://images.unsplash.com/photo-1485827404703-89b55fcc595e?q=80&w=400&auto=format&fit=crop" },
      { id: "2", title: "Drama Society", description: "Annual theatrical productions and national competitions.", photo: "https://images.unsplash.com/photo-1507676184212-d03ab07a01bf?q=80&w=400&auto=format&fit=crop" },
      { id: "3", title: "Basketball Team", description: "Our varsity team 'The Eagles' are the regional champions.", photo: "https://images.unsplash.com/photo-1546519638-68e109498ffc?q=80&w=400&auto=format&fit=crop" }
    ],
    facilities: (previewData?.facilities && previewData.facilities.length > 0) ? previewData.facilities : [
      { id: "1", categoryId: "Technology", title: "Modern Science Lab", description: "Fully equipped biology and chemistry laboratories.", photoFile: "https://images.unsplash.com/photo-1562774053-701939374585?q=80&w=400&auto=format&fit=crop" },
      { id: "2", categoryId: "Academic", title: "Digital Library", description: "Access to over 10,000 e-books and research journals.", photoFile: "https://images.unsplash.com/photo-1521587760476-6c12a7b041da?q=80&w=400&auto=format&fit=crop" },
      { id: "3", categoryId: "Sports", title: "Indoor Sports Arena", description: "Olympic-sized pool and multi-purpose gymnasium.", photoFile: "https://images.unsplash.com/photo-1534438327276-14e5300c3a48?q=80&w=400&auto=format&fit=crop" },
      { id: "4", categoryId: "Arts", title: "Creative Arts Center", description: "Soundproof music studios and fine arts workshops.", photoFile: "https://images.unsplash.com/photo-1460518451285-8f16c4f0340b?q=80&w=400&auto=format&fit=crop" },
      { id: "5", categoryId: "Medical", title: "Student Health Clinic", description: "24/7 medical services with trained resident nurses.", photoFile: "https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?q=80&w=400&auto=format&fit=crop" },
      { id: "6", categoryId: "Residential", title: "Premium Boarding Houses", description: "Comfortable, modern living spaces with high-speed internet.", photoFile: "https://images.unsplash.com/photo-1555854877-bab0e564b8d5?q=80&w=400&auto=format&fit=crop" }
    ],
    staff: (previewData?.staffMembers && previewData.staffMembers.length > 0) ? previewData.staffMembers : [
      { id: "1", title: "Mr.", name: "Peter Otieno", bio: "HOD Sciences", photoFile: "https://images.unsplash.com/photo-1560250097-0b93528c311a?q=80&w=256&auto=format&fit=crop" },
      { id: "2", title: "Ms.", name: "Sarah Wanjiku", bio: "HOD Humanities", photoFile: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?q=80&w=256&auto=format&fit=crop" },
      { id: "3", title: "Dr.", name: "David Kimani", bio: "HOD Languages", photoFile: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?q=80&w=256&auto=format&fit=crop" },
      { id: "4", title: "Mrs.", name: "Mary Atieno", bio: "HOD Mathematics", photoFile: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?q=80&w=256&auto=format&fit=crop" },
      { id: "5", title: "Mr.", name: "John Kamau", bio: "Sports Director", photoFile: "https://images.unsplash.com/photo-1566492031773-4f4e44671857?q=80&w=256&auto=format&fit=crop" },
      { id: "6", title: "Ms.", name: "Grace Njeri", bio: "Counselor", photoFile: "https://images.unsplash.com/photo-1580489944761-15a19d654956?q=80&w=256&auto=format&fit=crop" }
    ],
    board: (previewData?.boardMembers && previewData.boardMembers.length > 0) ? previewData.boardMembers : [
      { id: "1", title: "Rev.", name: "John Mark", role: "Chairman BOM", photoFile: "https://images.unsplash.com/photo-1463453091185-61582044d556?q=80&w=256&auto=format&fit=crop" },
      { id: "2", title: "Dr.", name: "Alice Mwangi", role: "Treasurer", photoFile: "https://images.unsplash.com/photo-1551836022-d5d88e9218df?q=80&w=256&auto=format&fit=crop" },
      { id: "3", title: "Hon.", name: "Samuel Korir", role: "Community Rep", photoFile: "https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?q=80&w=256&auto=format&fit=crop" },
      { id: "4", title: "Eng.", name: "Francis Njuguna", role: "Infrastructure", photoFile: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?q=80&w=256&auto=format&fit=crop" },
      { id: "5", title: "Prof.", name: "Jane Kemuntu", role: "Advisor", photoFile: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?q=80&w=256&auto=format&fit=crop" },
      { id: "6", title: "Lady Justice", name: "Ruth Kerubo", role: "Legal", photoFile: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?q=80&w=256&auto=format&fit=crop" }
    ],
    gallery: (previewData?.galleryItems && previewData.galleryItems.length > 0) ? previewData.galleryItems : [
      { id: "1", categoryId: "Campus", name: "Main Wing", description: "Our historic main administration building.", photoFile: "https://images.unsplash.com/photo-1525921429624-479b6a29d840?q=80&w=600&auto=format&fit=crop" },
      { id: "2", categoryId: "Events", name: "Sports Day", description: "Annual regional championships.", photoFile: "https://images.unsplash.com/photo-1511578314322-379afb476865?q=80&w=600&auto=format&fit=crop" },
      { id: "3", categoryId: "Infrastructure", name: "Library", description: "State-of-the-art research center.", photoFile: "https://images.unsplash.com/photo-1519331379826-f10be5486c6f?q=80&w=600&auto=format&fit=crop" },
      { id: "4", categoryId: "Students", name: "Tech Club", description: "Students building a robotics prototype.", photoFile: "https://images.unsplash.com/photo-1523240795612-9a054b0db644?q=80&w=600&auto=format&fit=crop" },
      { id: "5", categoryId: "Trips", name: "Mountain Hike", description: "Biology class exploring Mount Kenya flora.", photoFile: "https://images.unsplash.com/photo-1503220317375-aaad61436b1b?q=80&w=600&auto=format&fit=crop" }
    ],

    // 18. Contact & 19. Location
    contact: {
      phones: previewData?.phones || ["+254 712 345 678", "+254 789 012 345"],
      email: previewData?.email || "info@staugustines.ac.ke",
      whatsapp: previewData?.whatsapp || "254712345678",
      facebook: previewData?.fbLink || "",
      twitter: previewData?.twitterLink || "",
      instagram: previewData?.instaLink || "",
      address: previewData?.address || "Main Campus, Kisumu-Kakamega Road, Kisumu",
      mapsLink: previewData?.mapsLink || "https://www.google.com/maps/embed?pb=..."
    },

    // 21. Admissions & 22. Fees
    admissions: {
      process: previewData?.admissionProcess || "Our intake happens annually in January. Prospective students must submit their applications through our online portal or in-person at the Registrar's Office.",
      requirements: previewData?.admissionRequirements || ["Certified Birth Certificate", "KCPE Result Slip", "Leaving Certificate", "Medical Examination Report"],
      formFile: previewData?.admissionFormFile || null
    },
    payments: (previewData?.paymentMethods && previewData.paymentMethods.length > 0) ? previewData.paymentMethods : [
      { id: "1", method: "M-Pesa Paybill", details: "Business No: 123456 | Account: Student Name" },
      { id: "2", method: "KCB Bank", details: "Acc Name: St. Augustine's | Acc No: 0987654321" }
    ],
    feePolicy: previewData?.feeBreakdown || "School fees are due on or before the first day of each term. Payment plans are available upon request through the Finance Office."
  };

  const filteredGallery = data.gallery.filter((item: any) => activeTab === 'All' || (item.categoryId || item.category) === activeTab);

  return (
    <div ref={containerRef} className="bg-white text-slate-900 font-sans leading-relaxed selection:bg-blue-100 selection:text-blue-900 overflow-x-hidden">
      
      {/* Corporate Style Header */}
      <header className="relative w-full bg-slate-950 py-5 border-b border-white/5 z-50">
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
          <div className="flex items-center gap-3 cursor-pointer group">
            <div className="w-12 h-12 rounded-lg flex items-center justify-center transition-all bg-white/10 backdrop-blur-md group-hover:bg-white/20">
               {data.logo ? <img src={safeUrl(data.logo)} alt="School Logo" className="h-full w-full object-contain p-1" /> : <School size={24} className="text-white" />}
            </div>
            <div>
               <h2 className="text-lg font-bold tracking-tight leading-none text-white">{data.name}</h2>
               <p className="text-[9px] font-bold uppercase tracking-widest mt-1 text-white/50">{data.motto}</p>
            </div>
          </div>

          <nav className="hidden lg:flex items-center gap-8">
            {[
              { name: 'Home', href: '#' },
              { name: 'Contact', href: '#contact' },
              { name: 'About', href: '#about' },
              { name: 'Admission', href: '#admissions' },
              { name: 'Gallery', href: '#gallery' }
            ].map((item: any) => (
              <a 
                key={item.name} 
                href={item.href}
                className="text-[12px] font-bold uppercase tracking-widest transition-all text-white/80 hover:text-white"
              >
                {item.name}
              </a>
            ))}
          </nav>
        </div>
      </header>

      {/* Hero Section - Professional Scale */}
      <section className="relative min-h-[700px] lg:min-h-[850px] py-40 flex items-center overflow-hidden bg-slate-900">
        <div className="absolute inset-0 z-0 flex">
          {data.headerPhotos.length > 0 ? (
            data.headerPhotos.map((p: any, i: number) => (
              <img key={i} src={safeUrl(p)} alt={`Header Photo ${i + 1}`} className={`absolute inset-0 w-full h-full object-cover opacity-60 transition-opacity duration-1000 ${i === 0 ? 'opacity-60' : 'opacity-0'}`} />
            ))
          ) : (
            <img src="https://images.unsplash.com/photo-1523050854058-8df90110c9f1?q=80&w=2070&auto=format&fit=crop" alt="Default Hero Image" className="w-full h-full object-cover opacity-60" />
          )}
          <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-950/40 to-transparent"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 w-full">
          <div className="max-w-3xl space-y-8">
            <h1 className="text-5xl md:text-8xl font-bold text-white leading-[1] tracking-tight drop-shadow-2xl">
               {data.name}
            </h1>
            <p className="text-xl md:text-2xl text-slate-100 max-w-2xl font-medium leading-[1.6] italic border-l-4 border-blue-600 pl-8 drop-shadow-lg">
               "{data.motto}"
            </p>
          </div>
        </div>
      </section>

      {/* About Section - Cascading Image and Text Narrative Blocks */}
      <section id="about" className="py-24 bg-white scroll-mt-20 overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 space-y-32">
          
          {/* History Block (Text Left, Image Right) */}
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-8">
              <div className="space-y-4">
                <span className="text-blue-600 font-bold uppercase tracking-[0.2em] text-xs">Our Narrative</span>
                <h2 className="text-4xl font-bold text-slate-900 tracking-tight leading-tight">School History</h2>
                <div className="w-16 h-1 bg-blue-600 rounded-full"></div>
              </div>
              <p className="text-slate-600 leading-relaxed text-lg">{data.history}</p>
              <div className="grid grid-cols-2 gap-6 py-6">
                 <div><p className="text-3xl font-bold text-blue-900">{data.stats.students}</p><p className="text-[10px] uppercase font-bold text-slate-500 tracking-widest mt-1">Students</p></div>
                 <div><p className="text-3xl font-bold text-blue-900">{data.stats.staff}</p><p className="text-[10px] uppercase font-bold text-slate-500 tracking-widest mt-1">Staff Members</p></div>
                 <div><p className="text-3xl font-bold text-blue-900">{data.stats.founded}</p><p className="text-[10px] uppercase font-bold text-slate-500 tracking-widest mt-1">Founded</p></div>
                 <div><p className="text-3xl font-bold text-blue-900">{data.stats.alumni}</p><p className="text-[10px] uppercase font-bold text-slate-500 tracking-widest mt-1">Alumni Network</p></div>
              </div>
            </div>
            <div className="relative">
               <div className="aspect-[4/5] rounded-3xl overflow-hidden shadow-2xl relative z-10 border-8 border-white">
                 <img src={data.aboutPhoto} className="w-full h-full object-cover transition-transform duration-700 hover:scale-105" alt="School History Perspective" />
               </div>
               <div className="absolute -bottom-8 -left-8 w-64 h-64 bg-blue-50 z-0 rounded-[3rem] border border-blue-100 rotate-12"></div>
               <div className="absolute -top-8 -right-8 w-32 h-32 bg-slate-50 z-0 rounded-full"></div>
            </div>
          </div>

          {/* Vision & Mission Block (Images Left, Text Right) */}
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1 relative">
               <div className="grid grid-cols-2 gap-4 h-full relative z-10">
                  <div className="aspect-[3/4] rounded-3xl overflow-hidden shadow-xl mt-12 border-4 border-white">
                     <img src={data.visionPhoto} className="w-full h-full object-cover transition-transform duration-700 hover:scale-105" alt="Vision Inspiration" />
                  </div>
                  <div className="aspect-[3/4] rounded-3xl overflow-hidden shadow-xl border-4 border-white">
                     <img src={data.missionPhoto} className="w-full h-full object-cover transition-transform duration-700 hover:scale-105" alt="Mission In Action" />
                  </div>
               </div>
               <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[120%] h-[80%] bg-slate-50 z-0 rounded-[4rem] -rotate-6"></div>
            </div>
            <div className="order-1 lg:order-2 space-y-12">
              <div className="space-y-4">
                <span className="text-blue-600 font-bold uppercase tracking-[0.2em] text-xs">Core Principles</span>
                <h2 className="text-4xl font-bold text-slate-900 tracking-tight leading-tight">Vision & Mission</h2>
                <div className="w-16 h-1 bg-blue-600 rounded-full"></div>
              </div>
              <div className="space-y-10">
                 <div className="space-y-4">
                    <div className="w-12 h-12 bg-blue-50 text-blue-600 rounded-xl flex items-center justify-center shadow-sm"><Award size={24} /></div>
                    <h4 className="font-bold text-xl text-slate-900">Our Vision</h4>
                    <p className="text-slate-600 leading-relaxed text-lg">{data.vision}</p>
                 </div>
                 <div className="space-y-4">
                    <div className="w-12 h-12 bg-blue-50 text-blue-600 rounded-xl flex items-center justify-center shadow-sm"><Target size={24} /></div>
                    <h4 className="font-bold text-xl text-slate-900">Our Mission</h4>
                    <p className="text-slate-600 leading-relaxed text-lg">{data.mission}</p>
                 </div>
              </div>
            </div>
          </div>

          {/* Why Choose Us & Anthems (Text Left, Image Right) */}
          <div className="grid lg:grid-cols-2 gap-16 items-start">
             <div className="space-y-12">
                <div className="space-y-6">
                   <div className="space-y-4">
                     <span className="text-yellow-600 font-bold uppercase tracking-[0.2em] text-xs flex items-center gap-2"><HelpCircle size={14} /> Excellence</span>
                     <h2 className="text-4xl font-bold text-slate-900 tracking-tight leading-tight">Why Choose Us?</h2>
                     <div className="w-16 h-1 bg-yellow-500 rounded-full"></div>
                   </div>
                   <p className="text-slate-600 leading-relaxed text-lg">{data.whyUs}</p>
                </div>

                <div className="p-8 bg-slate-950 text-white rounded-[2rem] space-y-8 shadow-2xl relative overflow-hidden group">
                  <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500/20 rounded-full blur-[80px] transition-all duration-700 group-hover:bg-blue-400/30"></div>
                  <h4 className="font-bold uppercase text-xs tracking-[0.3em] text-blue-400 relative z-10">Institutional Anthems</h4>
                  <div className="grid sm:grid-cols-2 gap-4 relative z-10">
                    <div 
                      onClick={() => setExpandedView('anthems')}
                      className="p-6 bg-white/5 rounded-2xl border border-white/10 cursor-pointer hover:bg-white/10 transition-all hover:scale-[1.02]"
                    >
                      <div className="flex justify-between items-center mb-3">
                         <p className="text-sm font-black uppercase tracking-widest text-slate-300 transition-colors">National Anthem</p>
                         <ArrowUpRight size={16} className="text-blue-500" />
                      </div>
                      <p className="text-xs italic leading-relaxed text-slate-400 line-clamp-2">{data.nationalAnthem}</p>
                    </div>
                    <div 
                      onClick={() => setExpandedView('anthems')}
                      className="p-6 bg-white/5 rounded-2xl border border-white/10 cursor-pointer hover:bg-white/10 transition-all hover:scale-[1.02]"
                    >
                      <div className="flex justify-between items-center mb-3">
                         <p className="text-sm font-black uppercase tracking-widest text-slate-300 transition-colors">School Anthem</p>
                         <ArrowUpRight size={16} className="text-blue-500" />
                      </div>
                      <p className="text-xs italic leading-relaxed text-slate-400 line-clamp-2">{data.schoolAnthem}</p>
                    </div>
                  </div>
                </div>
             </div>
             <div className="relative h-full pt-10">
                <div className="aspect-square rounded-[3rem] overflow-hidden shadow-2xl border-8 border-white sticky z-10 top-32">
                  <img src={data.whyChooseUsPhoto} className="w-full h-full object-cover transition-transform duration-700 hover:scale-105" alt="Excellence in education" />
                </div>
                <div className="absolute top-0 right-10 w-full h-full bg-yellow-50 z-0 rounded-[4rem] -rotate-3"></div>
             </div>
          </div>

        </div>
      </section>

      {/* Academics & Curriculum */}
      <section id="academics" className="py-24 bg-slate-50 scroll-mt-20 border-y border-slate-100">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="relative order-2 lg:order-1">
               <div className="aspect-[4/3] rounded-[3rem] overflow-hidden shadow-2xl border-8 border-white sticky z-10 top-32">
                 <img src={data.curriculum.photo || "https://images.unsplash.com/photo-1577896851231-70ef18881754?q=80&w=2070"} className="w-full h-full object-cover transition-transform duration-700 hover:scale-105" alt="Classroom Curriculum" />
               </div>
               <div className="absolute -bottom-10 -right-10 w-full h-full bg-blue-100 z-0 rounded-[4rem] rotate-3"></div>
            </div>
            <div className="space-y-10 order-1 lg:order-2">
               <div className="space-y-6">
                 <span className="text-blue-600 font-bold uppercase tracking-[0.2em] text-xs">Academic Program</span>
                 <h2 className="text-4xl font-bold tracking-tight text-slate-900">Our Curriculum</h2>
                 <p className="text-slate-600 leading-relaxed text-lg">{data.curriculum.description}</p>
                 <div className="w-16 h-1 bg-blue-600 rounded-full"></div>
               </div>
               <div className="grid sm:grid-cols-2 gap-6">
                 <div className="p-8 bg-white border border-slate-100 rounded-[2rem] space-y-4 shadow-xl hover:shadow-2xl transition-all">
                   <h4 className="font-bold text-slate-900 border-b pb-4 flex items-center gap-3"><BookOpen size={20} className="text-blue-600" /> Core Subjects</h4>
                   <div className="flex flex-wrap gap-2 pt-2">
                     {data.curriculum.compulsory.map((s: string, i: number) => <span key={i} className="px-3 py-1.5 bg-slate-50 rounded-full text-[11px] font-bold text-slate-600 border border-slate-200 uppercase tracking-wider">{s}</span>)}
                   </div>
                 </div>
                 <div className="p-8 bg-gradient-to-br from-blue-600 to-blue-800 text-white rounded-[2rem] space-y-4 shadow-xl hover:shadow-2xl transition-all">
                   <h4 className="font-bold text-white border-b border-white/20 pb-4 flex items-center gap-3"><ArrowRight size={20} className="text-blue-200" /> Electives</h4>
                   <div className="flex flex-wrap gap-2 pt-2">
                     {data.curriculum.optional.map((s: string, i: number) => <span key={i} className="px-3 py-1.5 bg-white/10 backdrop-blur-md rounded-full text-[11px] font-bold text-blue-50 border border-white/20 uppercase tracking-wider">{s}</span>)}
                   </div>
                 </div>
               </div>
            </div>
          </div>
        </div>
      </section>

      {/* Clubs & Activities */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16 space-y-4">
            <h4 className="text-blue-600 font-black uppercase tracking-[0.3em] text-[10px]">Holistic Development</h4>
            <h2 className="text-4xl font-bold text-slate-900">Clubs & Activities</h2>
            <div className="w-16 h-1 bg-blue-600 rounded-full mx-auto"></div>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {data.activities.length > 0 ? data.activities.map((a: any, i: number) => (
              <div key={i} className="group border border-slate-100 rounded-2xl overflow-hidden hover:shadow-xl transition-all bg-white">
                <div className="h-48 overflow-hidden bg-slate-100 relative">
                  {a.photo ? <img src={safeUrl(a.photo)} className="w-full h-full object-cover group-hover:scale-105 transition-transform" alt="Activity" /> : <div className="w-full h-full flex items-center justify-center"><Trophy className="text-slate-300" size={40} /></div>}
                </div>
                <div className="p-6">
                  <h4 className="font-bold text-lg text-slate-900 mb-2">{a.title}</h4>
                  <p className="text-sm text-slate-500 leading-relaxed line-clamp-3">{a.description}</p>
                </div>
              </div>
            )) : (
              <div className="lg:col-span-3 text-center py-20 bg-slate-50 rounded-3xl border-2 border-dashed border-slate-200">
                <Trophy size={48} className="mx-auto text-slate-300 mb-4" />
                <p className="text-slate-500 font-bold uppercase tracking-widest text-xs">No Activities Added Yet</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Facilities */}
      <section className="py-24 bg-slate-50 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex justify-between items-end mb-16">
            <div className="space-y-4">
               <span className="text-blue-600 font-bold uppercase tracking-[0.2em] text-xs">School Infrastructure</span>
               <h2 className="text-4xl font-bold tracking-tight text-slate-900">Modern Facilities</h2>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {data.facilities.length > 0 ? data.facilities.slice(0, 4).map((f: any, i: number) => (
              <div key={i} className="bg-white p-4 rounded-2xl border border-slate-100 hover:border-blue-500 transition-all group shadow-sm">
                <div className="aspect-video rounded-xl overflow-hidden mb-4 bg-slate-100">
                  {f.photoFile ? <img src={safeUrl(f.photoFile)} className="w-full h-full object-cover group-hover:scale-105 transition-transform" alt="Facility" /> : <div className="w-full h-full flex items-center justify-center"><Building2 className="text-slate-300" /></div>}
                </div>
                <p className="text-[10px] font-black text-blue-600 uppercase tracking-widest mb-1 italic">{f.categoryId}</p>
                <h4 className="font-bold text-slate-900 mb-1">{f.title}</h4>
                <p className="text-[10px] text-slate-500 line-clamp-2 leading-relaxed">{f.description}</p>
              </div>
            )) : (
              <div className="lg:col-span-4 text-center py-20 bg-white rounded-3xl border-2 border-dashed border-slate-100 italic text-slate-400">
                School development details will appear here.
              </div>
            )}
          </div>
          {data.facilities.length > 4 && (
            <div className="mt-12">
               <button 
                  onClick={() => setExpandedView('facilities')} 
                  className="w-full py-5 border-2 border-dashed border-slate-200 rounded-xl text-xs font-black uppercase tracking-[0.3em] text-slate-500 hover:border-blue-500 hover:text-blue-600 transition-all flex items-center justify-center gap-2"
               >
                  View All Facilities <ArrowUpRight size={16} />
               </button>
            </div>
          )}
        </div>
      </section>

      {/* Principal's Segment */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-5 gap-16 items-center">
            <div className="lg:col-span-2 relative">
               <div className="aspect-[4/5] rounded-3xl overflow-hidden shadow-2xl bg-slate-100 border-8 border-slate-50">
                  {data.principal.photo ? <img src={safeUrl(data.principal.photo)} className="w-full h-full object-cover" alt="Principal" /> : <div className="w-full h-full flex items-center justify-center"><Users size={60} className="text-slate-300" /></div>}
               </div>
               <div className="absolute -bottom-6 -right-6 bg-blue-900 text-white p-8 rounded-3xl shadow-2xl hidden md:block">
                  <p className="text-sm font-bold uppercase tracking-[0.2em] mb-1">{data.principal.name}</p>
                  <p className="text-[10px] uppercase tracking-widest opacity-70">Chief Principal</p>
               </div>
            </div>
            <div className="lg:col-span-3 space-y-8">
               <div className="space-y-4">
                  <span className="text-blue-600 font-bold uppercase tracking-[0.2em] text-[10px]">Head of Institution</span>
                  <h2 className="text-4xl font-bold text-slate-900 tracking-tight">Principal's Address</h2>
                  <div className="w-16 h-1 bg-blue-600 rounded-full"></div>
               </div>
               <p className="text-xl text-slate-600 leading-relaxed italic font-light">
                 "{data.principal.message}"
               </p>
            </div>
          </div>
        </div>
      </section>

      {/* Staff & Board Directory */}
      <section id="staff" className="py-24 bg-slate-50 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-20">
             {/* Faculty */}
             <div className="space-y-12">
                <h4 className="text-xs font-black uppercase tracking-[0.3em] text-blue-600 border-l-4 border-blue-600 pl-4">Academic Faculty</h4>
                <div className="grid grid-cols-2 gap-6">
                  {data.staff.slice(0, 4).map((s: any, i: number) => (
                    <div key={i} className="flex items-center gap-4 group">
                      <div className="w-16 h-16 rounded-full overflow-hidden bg-slate-200 shrink-0 border-2 border-white shadow-sm flex items-center justify-center">
                        {s.photoFile ? <img src={safeUrl(s.photoFile)} alt={s.name || "Staff Member"} className="w-full h-full object-cover" /> : <Users size={24} className="text-slate-400" />}
                      </div>
                      <div>
                        <h5 className="font-bold text-slate-900 text-sm leading-none mb-1">{s.title} {s.name}</h5>
                        <p className="text-[10px] font-bold text-blue-500 uppercase tracking-widest">{s.bio || "Staff"}</p>
                      </div>
                    </div>
                  ))}
                </div>
                {data.staff.length > 4 && (
                  <button 
                    onClick={() => setExpandedView('staff')} 
                    className="w-full py-4 border-2 border-dashed border-slate-200 rounded-xl text-[10px] font-black uppercase tracking-[0.3em] text-slate-500 hover:border-blue-500 hover:text-blue-600 transition-all flex items-center justify-center gap-2"
                  >
                    View All Faculty <ArrowUpRight size={14} />
                  </button>
                )}
             </div>
             {/* Board */}
             <div className="space-y-12">
                <h4 className="text-xs font-black uppercase tracking-[0.3em] text-blue-600 border-l-4 border-blue-600 pl-4">Board of Management</h4>
                <div className="grid grid-cols-2 gap-6">
                  {data.board.slice(0, 4).map((b: any, i: number) => (
                    <div key={i} className="flex items-center gap-4 group">
                      <div className="w-16 h-16 rounded-full overflow-hidden bg-slate-200 shrink-0 border-2 border-white shadow-sm flex items-center justify-center">
                        {b.photoFile ? <img src={safeUrl(b.photoFile)} alt={b.name || "Board Member"} className="w-full h-full object-cover" /> : <School size={24} className="text-slate-400" />}
                      </div>
                      <div>
                        <h5 className="font-bold text-slate-900 text-sm leading-none mb-1">{b.title} {b.name}</h5>
                        <p className="text-[10px] font-bold text-blue-500 uppercase tracking-widest">{b.role}</p>
                      </div>
                    </div>
                  ))}
                </div>
                {data.board.length > 4 && (
                  <button 
                    onClick={() => setExpandedView('board')} 
                    className="w-full py-4 border-2 border-dashed border-slate-200 rounded-xl text-[10px] font-black uppercase tracking-[0.3em] text-slate-500 hover:border-blue-500 hover:text-blue-600 transition-all flex items-center justify-center gap-2"
                  >
                    View All Board <ArrowUpRight size={14} />
                  </button>
                )}
             </div>
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section id="gallery" className="py-24 bg-slate-50 scroll-mt-20 border-t border-slate-100">
        <div className="max-w-7xl mx-auto px-6 mb-12">
           <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
              <div className="space-y-4">
                 <span className="text-blue-600 font-bold uppercase tracking-[0.2em] text-xs">Visual Archive</span>
                 <h2 className="text-4xl font-bold tracking-tight text-slate-900">Student Life</h2>
              </div>
              
              {/* Category Nav - "All" + Dynamic Form Categories */}
              <div className="flex flex-wrap gap-2">
                 {['All', ...Array.from(new Set(data.gallery.map((g: any) => g.categoryId || g.category).filter(Boolean)))].map((cat: any) => (
                    <button 
                       key={cat} 
                       onClick={() => setActiveTab(cat)}
                       className={`px-5 py-2 rounded-full text-[10px] font-black uppercase tracking-[0.1em] transition-all border ${activeTab === cat ? 'bg-blue-600 text-white border-blue-600 shadow-md' : 'bg-white text-slate-500 border-slate-200 hover:border-blue-400 hover:text-blue-600'}`}
                    >
                       {cat}
                    </button>
                 ))}
              </div>
           </div>
        </div>
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
           {filteredGallery.slice(0, 4).map((item: any, i: number) => (
             <div key={i} onClick={() => setLightboxIndex(i)} className="cursor-pointer relative overflow-hidden rounded-2xl group shadow-sm aspect-square bg-slate-200 flex flex-col">
                {item.photoFile || item.photo ? <img src={safeUrl(item.photoFile || item.photo)} alt={item.name || item.title || "Gallery Image"} className="absolute inset-0 w-full h-full object-cover transition-all duration-700 group-hover:scale-110" /> : <div className="absolute inset-0 w-full h-full bg-slate-100 flex items-center justify-center text-slate-400"><ImageIcon size={40} /></div>}
                
                {/* Dark Hover Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-900/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-6">
                   <p className="text-[9px] font-black text-blue-400 uppercase tracking-widest mb-1.5">{item.categoryId || item.category}</p>
                   {item.name ? <h4 className="text-white font-bold text-lg leading-tight mb-1">{item.name}</h4> : null}
                   {item.description ? <p className="text-slate-300 text-[11px] leading-relaxed line-clamp-3">{item.description}</p> : null}
                </div>
             </div>
           ))}
        </div>
        {filteredGallery.length > 4 && (
          <div className="max-w-7xl mx-auto px-6 mt-12">
             <button 
                onClick={() => setExpandedView('gallery')} 
                className="w-full py-5 border-2 border-dashed border-slate-200 rounded-xl text-xs font-black uppercase tracking-[0.3em] text-slate-500 hover:border-blue-500 hover:text-blue-600 transition-all flex items-center justify-center gap-2"
             >
                View All Visuals <ArrowUpRight size={16} />
             </button>
          </div>
        )}
      </section>

      {/* Admissions & Fees */}
      <section id="admissions" className="py-24 bg-slate-950 text-white">
         <div className="max-w-7xl mx-auto px-6">
            <div className="grid lg:grid-cols-2 gap-20 items-start">
               <div className="space-y-12">
                  <div className="space-y-4">
                     <h2 className="text-4xl font-bold tracking-tight">Admissions</h2>
                     <p className="text-blue-400 font-bold uppercase tracking-widest text-[10px]">Enrollment Process</p>
                  </div>
                  <div className="space-y-8">
                     <p className="text-slate-400 leading-relaxed text-lg italic border-l-4 border-blue-600 pl-6">
                        {data.admissions.process}
                     </p>
                     <div className="space-y-4">
                        <h4 className="text-sm font-bold uppercase tracking-widest text-slate-500">General Requirements</h4>
                        <ul className="space-y-3">
                           {data.admissions.requirements.map((req: string, i: number) => (
                              <li key={i} className="flex items-center gap-3 text-sm text-slate-300">
                                 <CheckCircle2 size={16} className="text-blue-500" /> {req}
                              </li>
                           ))}
                        </ul>
                     </div>
                  </div>
               </div>
               <div className="space-y-12 bg-white/5 p-10 rounded-3xl border border-white/10 backdrop-blur-sm">
                  <div className="space-y-4">
                     <h2 className="text-4xl font-bold tracking-tight">Fees & Finance</h2>
                     <p className="text-blue-400 font-bold uppercase tracking-widest text-[10px]">Payment Channels</p>
                  </div>
                  <div className="space-y-6">
                     {data.payments.map((p: any, i: number) => (
                        <div key={i} className="flex items-center justify-between p-6 bg-white/5 border border-white/10 rounded-xl">
                           <div>
                              <p className="text-[10px] font-black text-blue-500 uppercase tracking-widest mb-1 italic underline">{p.method}</p>
                              <p className="text-lg font-bold">{p.details}</p>
                           </div>
                           <ArrowUpRight size={20} className="text-slate-600" />
                        </div>
                     ))}
                     <div className="p-6 border-l-4 border-blue-600 bg-blue-600/10 rounded-r-xl">
                        <p className="text-xs text-slate-400 leading-relaxed uppercase tracking-widest font-black mb-2 italic">Policy Overview</p>
                        <p className="text-[10px] font-bold text-slate-300 italic">"{data.feePolicy}"</p>
                     </div>
                  </div>
               </div>
            </div>
         </div>
      </section>

      {/* Quote & Values */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
             <div className="space-y-8 p-12 bg-slate-50 rounded-[40px] border border-slate-100">
                <Quote className="text-blue-600 opacity-20" size={60} />
                <p className="text-2xl font-light italic leading-relaxed text-slate-600">
                  "{data.quote.text}"
                </p>
                <div className="flex items-center gap-4">
                   <div className="w-12 h-1 bg-blue-600 rounded-full"></div>
                   <p className="text-sm font-bold uppercase tracking-[0.2em] text-slate-900">{data.quote.author}</p>
                </div>
             </div>
             <div className="space-y-10">
                <div className="space-y-4">
                   <span className="text-blue-600 font-bold uppercase tracking-[0.2em] text-[10px]">Our Character</span>
                   <h2 className="text-4xl font-bold text-slate-900">Core Values</h2>
                </div>
                <div className="grid grid-cols-2 gap-4">
                   {data.coreValues.map((v: string, i: number) => (
                      <div key={i} className="p-6 border border-slate-100 rounded-2xl flex items-center gap-4 bg-white shadow-sm hover:border-blue-500 transition-all">
                         <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                         <span className="text-sm font-bold text-slate-700 uppercase tracking-wide">{v}</span>
                      </div>
                   ))}
                </div>
             </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer id="contact" className="bg-slate-950 text-white pt-24 pb-12 scroll-mt-20">
         <div className="max-w-7xl mx-auto px-6">
            <div className="grid lg:grid-cols-3 gap-20 mb-20">
               <div className="space-y-8">
                  <div className="flex items-center gap-3">
                     <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center font-bold">
                       {data.logo ? <img src={safeUrl(data.logo)} alt="School Logo" className="h-full w-full object-contain p-1" /> : data.name.charAt(0)}
                     </div>
                     <h2 className="text-2xl font-bold tracking-tight uppercase leading-none">{data.name}</h2>
                  </div>
                  <p className="text-sm text-slate-400 leading-relaxed font-medium italic">
                    "{data.motto}"
                  </p>
                  <div className="flex flex-col gap-4">
                      {data.contact.whatsapp && (
                        <a href={`https://wa.me/${data.contact.whatsapp}`} target="_blank" className="bg-green-600/20 border border-green-500/30 text-green-400 px-6 py-3 rounded-lg font-bold text-xs tracking-widest hover:bg-green-600/30 transition-all flex items-center gap-2 w-fit">
                           <MessageCircle size={16} /> CHAT ON WHATSAPP
                        </a>
                      )}
                      <div className="flex gap-4">
                        {data.contact.facebook && <a href={data.contact.facebook} aria-label="Facebook" title="Facebook" className="w-10 h-10 rounded-lg border border-white/10 flex items-center justify-center hover:bg-blue-600 transition-all font-bold tracking-widest"><Facebook size={18} /></a>}
                        {data.contact.instagram && <a href={data.contact.instagram} aria-label="Instagram" title="Instagram" className="w-10 h-10 rounded-lg border border-white/10 flex items-center justify-center hover:bg-blue-600 transition-all font-bold tracking-widest"><Instagram size={18} /></a>}
                        {data.contact.twitter && <a href={data.contact.twitter} aria-label="Twitter" title="Twitter" className="w-10 h-10 rounded-lg border border-white/10 flex items-center justify-center hover:bg-blue-600 transition-all font-bold tracking-widest"><X size={18} /></a>}
                      </div>
                  </div>
               </div>

                <div className="grid sm:grid-cols-2 gap-10 lg:col-span-2">
                  <div className="space-y-6">
                     <h4 className="text-xs font-black uppercase tracking-[0.2em] text-blue-500">Contact Official</h4>
                     <ul className="space-y-4">
                        {data.contact.phones.map((p: string, i: number) => <li key={i} className="flex items-center gap-3 text-sm text-slate-400 font-medium"><Phone size={16} className="text-blue-500" /> {p}</li>)}
                        <li className="flex items-center gap-3 text-sm text-slate-400 font-medium"><Mail size={16} className="text-blue-500" /> {data.contact.email}</li>
                        <li className="flex items-start gap-3 text-sm text-slate-400 font-medium"><MapPin size={16} className="text-blue-500 shrink-0" /> {data.contact.address}</li>
                     </ul>
                  </div>
                  <div className="space-y-6">
                     <h4 className="text-xs font-black uppercase tracking-[0.2em] text-blue-500">Institutional Maps</h4>
                     <div className="h-40 bg-white/5 rounded-2xl border border-white/10 overflow-hidden relative">
                        {data.contact.mapsLink ? (
                           <iframe title="School location map" src={data.contact.mapsLink} className="w-full h-full border-none grayscale pointer-events-none" />
                        ) : (
                           <div className="w-full h-full flex items-center justify-center text-slate-500 text-xs italic">
                              Map location coordinate not provided.
                           </div>
                        )}
                        <a href={data.contact.mapsLink} aria-label="Open maps location" title="Open maps location" className="absolute bottom-2 right-2 bg-blue-600 text-white p-2 rounded-lg"><ArrowUpRight size={14} /></a>
                     </div>
                  </div>
               </div>
            </div>

            <div className="pt-10 border-t border-white/10 flex justify-between items-center">
               <p className="text-[10px] font-bold text-slate-600 uppercase tracking-[0.4em]">&copy; {new Date().getFullYear()} {data.name}. All Rights Reserved.</p>
               <button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} aria-label="Scroll to top" title="Scroll to top" className="w-12 h-12 rounded-lg bg-blue-600 flex items-center justify-center hover:bg-blue-500 transition-all shadow-xl">
                  <ChevronDown className="text-white rotate-180" size={24} />
               </button>
            </div>
         </div>
      </footer>

      <style>{`
        .animate-scroll { display: flex; animation: scroll 60s linear infinite; }
        @keyframes scroll { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }
      `}</style>
      
      {/* Expanded Directory Overlay */}
      {expandedView && (
        <div className="fixed inset-0 z-[200] bg-white overflow-y-auto pb-24">
           <div className="bg-white border-b px-6 py-10 flex items-center justify-between">
              <div className="flex items-center gap-4">
                 <button onClick={() => setExpandedView(null)} aria-label="Go back" title="Go back" className="w-10 h-10 rounded-full border border-slate-200 flex items-center justify-center hover:bg-slate-50 transition-all">
                    <ChevronLeft size={24} />
                 </button>
                 <div>
                    <h2 className="text-xl font-bold tracking-tight text-slate-900">
                       {expandedView === 'staff' ? 'Academic Faculty Directory' : expandedView === 'gallery' ? 'Visual Archive' : expandedView === 'facilities' ? 'School Facilities Directory' : expandedView === 'anthems' ? 'Institutional Anthems' : 'Board of Management Directory'}
                    </h2>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mt-0.5">
                       {data.name}
                    </p>
                 </div>
              </div>
              <button onClick={() => setExpandedView(null)} aria-label="Close" title="Close" className="text-slate-400 hover:text-slate-900"><X size={24} /></button>
           </div>
           
           <div className="max-w-7xl mx-auto px-6 py-16">
               {expandedView === 'gallery' ? (
                 <>
                   <div className="flex flex-wrap gap-2 justify-center mb-10">
                      {['All', ...Array.from(new Set(data.gallery.map((g: any) => g.categoryId || g.category).filter(Boolean)))].map((cat: any) => (
                         <button 
                            key={cat} 
                            onClick={() => setActiveTab(cat)}
                            className={`px-5 py-2 rounded-full text-[10px] font-black uppercase tracking-[0.1em] transition-all border ${activeTab === cat ? 'bg-blue-600 text-white border-blue-600 shadow-md' : 'bg-slate-50 text-slate-500 border-slate-200 hover:border-blue-400 hover:text-blue-600'}`}
                         >
                            {cat}
                         </button>
                      ))}
                   </div>
                   <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                     {filteredGallery.map((item: any, i: number) => (
                     <div key={i} onClick={() => setLightboxIndex(i)} className="cursor-pointer relative overflow-hidden rounded-2xl group shadow-sm aspect-square bg-slate-200 flex flex-col">
                        {item.photoFile || item.photo ? <img src={safeUrl(item.photoFile || item.photo)} alt={item.name || item.title || "Gallery Image"} className="absolute inset-0 w-full h-full object-cover transition-all duration-700 group-hover:scale-110" /> : <div className="absolute inset-0 w-full h-full bg-slate-100 flex items-center justify-center text-slate-400"><ImageIcon size={40} /></div>}
                        <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-900/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-6">
                           <p className="text-[9px] font-black text-blue-400 uppercase tracking-widest mb-1.5">{item.categoryId || item.category}</p>
                           {item.name ? <h4 className="text-white font-bold text-lg leading-tight mb-1">{item.name}</h4> : null}
                           {item.description ? <p className="text-slate-300 text-[11px] leading-relaxed line-clamp-3">{item.description}</p> : null}
                        </div>
                     </div>
                   ))}
                 </div>
                 </>
               ) : expandedView === 'facilities' ? (
                 <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                   {data.facilities.map((f: any, i: number) => (
                     <div key={i} className="bg-white p-4 rounded-2xl border border-slate-100 hover:border-blue-500 transition-all group shadow-sm text-left">
                       <div className="aspect-video rounded-xl overflow-hidden mb-4 bg-slate-100">
                         {f.photoFile ? <img src={safeUrl(f.photoFile)} className="w-full h-full object-cover group-hover:scale-105 transition-transform" alt="Facility" /> : <div className="w-full h-full flex items-center justify-center"><Building2 className="text-slate-300" /></div>}
                       </div>
                       <p className="text-[10px] font-black text-blue-600 uppercase tracking-widest mb-1 italic">{f.categoryId}</p>
                       <h4 className="font-bold text-slate-900 mb-1">{f.title}</h4>
                       <p className="text-[10px] text-slate-500 leading-relaxed">{f.description}</p>
                     </div>
                   ))}
                 </div>
               ) : expandedView === 'anthems' ? (
                 <div className="max-w-5xl mx-auto space-y-24">
                    <div className="space-y-12">
                       <div className="text-center space-y-4">
                          <span className="text-blue-600 font-bold uppercase tracking-[0.2em] text-xs">The Republic of Kenya</span>
                          <h3 className="text-3xl md:text-4xl font-bold text-slate-900">National Anthem</h3>
                       </div>
                       <div className="aspect-video w-full rounded-3xl overflow-hidden shadow-xl bg-slate-100 relative">
                          <img 
                             src="https://images.unsplash.com/photo-1608925565154-2974eb36e6ba?q=80&w=2070" 
                             className="w-full h-full object-cover" 
                             alt="National Anthem presentation backdrop"
                          />
                          <div className="absolute inset-0 bg-slate-900/40"></div>
                       </div>
                       <div className="bg-slate-50 p-10 md:p-16 rounded-3xl border border-slate-100 text-center shadow-sm">
                          <p className="whitespace-pre-line text-lg md:text-xl text-slate-700 leading-loose italic font-medium">
                             {data.nationalAnthem}
                          </p>
                       </div>
                    </div>
                    
                    <div className="space-y-12 pt-16 border-t border-slate-200">
                       <div className="text-center space-y-4">
                          <span className="text-blue-600 font-bold uppercase tracking-[0.2em] text-xs">{data.name}</span>
                          <h3 className="text-3xl md:text-4xl font-bold text-slate-900">School Anthem</h3>
                       </div>
                       <div className="aspect-video w-full rounded-3xl overflow-hidden shadow-xl bg-slate-100 relative">
                          <img 
                             src="https://images.unsplash.com/photo-1523050854058-8df90110c9f1?q=80&w=2070" 
                             className="w-full h-full object-cover" 
                             alt="School Anthem presentation backdrop"
                          />
                          <div className="absolute inset-0 bg-slate-900/40"></div>
                       </div>
                       <div className="bg-slate-50 p-10 md:p-16 rounded-3xl border border-slate-100 text-center shadow-sm">
                          <p className="whitespace-pre-line text-lg md:text-xl text-slate-700 leading-loose italic font-medium">
                             {data.schoolAnthem}
                          </p>
                       </div>
                    </div>
                 </div>
               ) : (
                 <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-12">
                    {(expandedView === 'staff' ? data.staff : data.board).map((item: any, i: number) => (
                       <div key={i} className="flex flex-col items-center text-center space-y-4">
                          <div className="w-40 h-40 rounded-3xl overflow-hidden bg-slate-50 border shadow-inner flex items-center justify-center text-slate-200">
                             {(item.photoFile || item.photo) ? (
                                <img src={safeUrl(item.photoFile || item.photo)} alt={item.name || "Profile Image"} className="w-full h-full object-cover" />
                             ) : (
                                <div className="w-full h-full flex items-center justify-center text-slate-200">
                                   {expandedView === 'staff' ? <Users size={60} /> : <School size={60} />}
                                </div>
                             )}
                          </div>
                          <div>
                             <h3 className="font-bold text-slate-900 uppercase tracking-wide text-sm">{item.title} {item.name}</h3>
                             <p className="text-[10px] font-black text-blue-600 uppercase tracking-[0.2em] mt-1">{expandedView === 'staff' ? (item.bio || 'Faculty') : item.role}</p>
                          </div>
                       </div>
                    ))}
                 </div>
               )}
            </div>
         </div>
      )}

      {/* Fullscreen Lightbox Overlay */}
      {lightboxIndex !== null && filteredGallery[lightboxIndex] && (
        <div className="fixed inset-0 bg-black flex items-center justify-center" style={{ zIndex: 99999 }}>
           <button 
               onClick={() => setLightboxIndex(null)} 
               className="absolute top-8 left-8 z-50 text-white/70 hover:text-white transition-all flex items-center gap-2 font-bold uppercase tracking-widest text-[11px] bg-black/40 px-6 py-3 rounded-full backdrop-blur-sm border border-white/10"
           >
              <ChevronLeft size={16} /> Back to Gallery
           </button>
           
           <div className="absolute inset-0 w-full h-full">
              {filteredGallery[lightboxIndex].photoFile || filteredGallery[lightboxIndex].photo ? (
                 <img src={safeUrl(filteredGallery[lightboxIndex].photoFile || filteredGallery[lightboxIndex].photo)} alt={filteredGallery[lightboxIndex].name || "Gallery Image"} className="w-full h-full object-contain" />
              ) : (
                 <div className="w-full h-full bg-slate-900 flex items-center justify-center text-white/20"><ImageIcon size={80} /></div>
              )}
           </div>

           {/* Title & Description Overlay */}
           <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-40 bg-black/70 backdrop-blur-xl border border-white/10 px-8 py-5 rounded-2xl max-w-2xl w-[90%] text-center shadow-2xl">
              <p className="text-[9px] font-black text-blue-400 uppercase tracking-[0.2em] mb-1">{filteredGallery[lightboxIndex].categoryId || filteredGallery[lightboxIndex].category}</p>
              <h3 className="text-white font-bold text-xl tracking-tight leading-tight mb-2">{filteredGallery[lightboxIndex].name || "Untitled Image"}</h3>
              <p className="text-slate-300 text-xs leading-relaxed hidden md:block">{filteredGallery[lightboxIndex].description || "School Visuals"}</p>
           </div>

           {/* Prev / Next Flow */}
           <div className="absolute inset-0 pointer-events-none flex items-center justify-between px-6 md:px-12 z-20">
              {lightboxIndex > 0 ? (
                <button 
                  onClick={(e) => { e.stopPropagation(); setLightboxIndex(lightboxIndex - 1); }} 
                  className="pointer-events-auto text-white/60 hover:text-white transition-all bg-black/40 hover:bg-black/60 p-6 rounded-full font-black uppercase text-xs tracking-[0.3em] backdrop-blur-md shadow-2xl border border-white/10 hover:scale-105"
                >
                  Prev
                </button>
              ) : <div />}
              
              {lightboxIndex < filteredGallery.length - 1 ? (
                <button 
                  onClick={(e) => { e.stopPropagation(); setLightboxIndex(lightboxIndex + 1); }} 
                  className="pointer-events-auto text-white/60 hover:text-white transition-all bg-black/40 hover:bg-black/60 p-6 rounded-full font-black uppercase text-xs tracking-[0.3em] backdrop-blur-md shadow-2xl border border-white/10 hover:scale-105"
                >
                  Next
                </button>
              ) : <div />}
           </div>
        </div>
      )}
    </div>
  );
};

export default Design0_Features;
